import HighchartsReact from "highcharts-react-official";
import SurveyReportMetric from "../../../models/surveyReportMetric";
import Highcharts, { Point } from "highcharts";
import { useRef, useState, useEffect } from "react";
import { api } from "../../../store/api";
import SurveyReport from "../../../models/surveyReport";
import SurveyReportSchool from "../../../models/surveyReportSchool";
import { SurveyReportDataMetricResponse } from "../../../models/surveyReportDataMetricResponse";
import { SurveyReportDataMetricDetailResponse } from "../../../models/surveyReportDataMetricDetailResponse";
import { SurveyReportStakeholder } from "../../../models/surveyReportStakeholder";
import FileBlob from "../../../models/fileBlob";

type MetricsByDomainProps = {
  report: SurveyReport;
  reportSchool: SurveyReportSchool;

  domain: string;
  metrics: SurveyReportMetric[];
}
export default function MetricsByDomain(props: MetricsByDomainProps) {
  const { report, reportSchool, domain, metrics } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [metricData, setMetricData] = useState<SurveyReportDataMetricResponse>();
  const [rootStakeholders, setRootStakeholders] = useState<SurveyReportStakeholder[]>([]);
  const [parentStakeholders, setParentStakeholders] = useState<SurveyReportStakeholder[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<SurveyReportMetric>();

  useEffect(() => {
    setIsLoading(true);
    setMetricData(undefined);
    api.survey(report.surveyId).report(report.id).school(reportSchool.id!)
      .metric({
        domain: domain,
        school: reportSchool.id!,
        //stakeholder: stakeholder?.surveyStakeholderId,
      })
      .then((data) => {
        const filteredStakeholders = data.stakeholders.filter(s => (s.parentSurveyStakeholderId ?? 0) === 0);
        setRootStakeholders(filteredStakeholders);
        setParentStakeholders(filteredStakeholders.filter(s => data.stakeholders.find(sub => sub.parentSurveyStakeholderId === s.surveyStakeholderId)));
        setMetricData(data);
        setIsLoading(false);
      })
      .catch((reason) => {
        //setError("Unable to load report schools/systems: " + reason);
        setIsLoading(false);
      });

  }, [domain, report.id, report.surveyId, reportSchool.id]);

  return <div className="my-2">
    <h2 className="text-center">{domain}</h2>
    {isLoading && <div className="alert alert-warning"><i className="fa-solid fa-spin fa-spinner"></i> <strong>Loading {domain}</strong></div>}
    {metricData && <OverallSummary domain={domain} rootStakeholders={rootStakeholders} parentStakeholders={parentStakeholders} data={metricData} />}
    {metricData && <MetricSummary domain={domain} rootStakeholders={rootStakeholders} parentStakeholders={parentStakeholders} data={metricData} onMetricSelected={(metricName) => setSelectedMetric(metrics.find(m => m.name === metricName))} />}
    {metricData && <MetricSubScale report={report} reportSchool={reportSchool} domain={domain} rootStakeholders={rootStakeholders} parentStakeholders={parentStakeholders} data={metricData} selectedMetric={selectedMetric} onMetricSelected={(metricName) => setSelectedMetric(metrics.find(m => m.name === metricName))} />}
  </div>
}


type OverallSummaryProps = {
  domain: string;
  data: SurveyReportDataMetricResponse;
  rootStakeholders: SurveyReportStakeholder[];
  parentStakeholders: SurveyReportStakeholder[];
  onMetricSelected?: (metricName: string) => void;
  selectedMetric?: SurveyReportMetric;
}

function OverallSummary(props: OverallSummaryProps) {
  const { data, domain, rootStakeholders, parentStakeholders } = props;
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  //const [chartOptions, setChartOptions] = useState<Highcharts.Options>();
  //const [isLoading, setIsLoading] = useState(false);


  const chartOptions: Highcharts.Options = {
    chart: {
      type: 'column',
      //type: 'line',
    },
    title: {
      text: `Overall ${domain}`,
    },
    xAxis: {
      type: 'category'
    },
    tooltip: {
      valueSuffix: '',
      valueDecimals: 2,

    },
    yAxis: [
      {
        labels: {
          //format: '{value}%',
          style: {
            //color: Highcharts.getOptions().colors[1]
          }
        },
        min: 0,
        //softMax: 8,
        max: 5,
        title: {
          text: 'Average Score'
        },
      }],
    plotOptions: {
      series: {
        dataLabels: {
          enabled: true,
          format: '{y:.2f}'
        }
      }
    },
    series: [
      {
        type: 'column',
        name: 'All Stakeholders',
        colorByPoint: true,
        tooltip: {
          valueDecimals: 2,
          pointFormatter: function (this: Point): string {
            if (this.y !== undefined) {
              // TODO how many responses

              // const resource = data.resources[this.x];
              // const tag = topics[this.y];
              // const parentTag = tags?.find((d) => d.id === tag.parentTagId);
              // return `${resource.name} ${(this.value ?? 0) === 0 ? 'does not have' : 'has'} tag '${parentTag?.label} > ${tag.label}' in common`;
            }
            return '';
          },
        },

        data: rootStakeholders.map(s => {
          const anySubstakeholders = data.stakeholders
            .find(sub => sub.parentSurveyStakeholderId === s.surveyStakeholderId);
          // console.log('stakeholder vs. anysubs?', s, anySubstakeholders);
          const metricScores = data.scores.filter(score => score.stakeholderId === s.surveyStakeholderId);
          const sumScores = metricScores.map(metricScore => metricScore.sumValue).reduce((a, b) => a + b);
          const countResponses = metricScores.map(metricScore => metricScore.countResponses).reduce((a, b) => a + b)
          return {
            name: s.name,
            y: countResponses > 0 ? sumScores / countResponses : undefined,
            drilldown: anySubstakeholders ? s.name : undefined,
          };
        }),
      },
    ],
    drilldown: {
      breadcrumbs: {
        position: {
          align: 'right'
        }
      },
      series: parentStakeholders.map<Highcharts.SeriesOptionsType>((parent) => {
        const metricScores = data.scores.filter(score => score.stakeholderId === parent.surveyStakeholderId);
        const sumScores = metricScores.map(metricScore => metricScore.sumValue).reduce((a, b) => a + b);
        const countResponses = metricScores.map(metricScore => metricScore.countResponses).reduce((a, b) => a + b)

        const subStakeholders = data.stakeholders
          .filter(sub => sub.parentSurveyStakeholderId === parent.surveyStakeholderId);
        return {
          name: parent.name,
          id: parent.name,
          type: 'column',
          colorByPoint: true,
          data: [
            {
              name: `All ${parent.name}`,
              y: countResponses > 0 ? sumScores / countResponses : undefined,
            },
            ...subStakeholders.map<Highcharts.PointOptionsObject>((sub) => {
              const metricScores = data.scores.filter(score => score.stakeholderId === sub.surveyStakeholderId);
              const sumScores = metricScores.map(metricScore => metricScore.sumValue).reduce((a, b) => a + b);
              const countResponses = metricScores.map(metricScore => metricScore.countResponses).reduce((a, b) => a + b)

              return {
                name: sub.name,
                //type: 'xrange',
                y: countResponses > 0 ? sumScores / countResponses : undefined,
              };
            }),
          ],
        };
      }
      ),


      // series: [
      //   {
      //     name: 'Parents',
      //     id: 'Parents',
      //     type: 'column',
      //     colorByPoint: true,
      //     data: [
      //       { name: 'Parents', y: 4.3 },
      //       { name: 'College Educated', y: 4.8 },
      //       { name: 'White Males', y: 3.8 },
      //       { name: 'Black Females', y: 3.8 },
      //     ],
      //   }
      // ],
    },
  };

  return <div className="my-3">
    <div className="card">
      <div className="card-body">
        {chartOptions && <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartComponentRef} />}
      </div>
    </div>
  </div>;
}


function MetricSummary(props: OverallSummaryProps) {
  const { data, domain, rootStakeholders, onMetricSelected } = props;
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);

  const metrics = [...new Set(data.scores.map(score => score.metricName))];

  const chartOptions = {
    chart: {
      type: 'column',
      //type: 'line',
    },
    title: {
      text: domain,
    },
    xAxis: {
      //type: 'category',
      categories: metrics,
      labels: {
        events: {
          click: function (this: Highcharts.AxisLabelsFormatterContextObject) {
            if (onMetricSelected) {
              console.log('onMetricSelected', this.value, metrics[this.value as number]);
              onMetricSelected(metrics[this.value as number]);
            }
          },
        },
        style: {
          cursor: 'pointer',
          color: '#0d6efd',
          textDecoration: 'underline',
        },
      },
    },
    yAxis: [
      {
        labels: {
          //format: '{value}%',
          style: {
            //color: Highcharts.getOptions().colors[1]
          }
        },
        min: 0,
        //softMax: 8,
        max: 5,
        title: {
          text: 'Average Score'
        },
      }],
    plotOptions: {
      series: {
        dataLabels: {
          enabled: true,
          format: '{y:.2f}'
        }
      }
    },
    series: rootStakeholders.map((stakeholder) => {
      return {
        name: stakeholder.name,
        tooltip: {
          valueDecimals: 2,
          pointFormatter: function (this: Point): string {
            if (this.y !== undefined) {
              // TODO how many responses, for what stakeholder group

              // const resource = data.resources[this.x];
              // const tag = topics[this.y];
              // const parentTag = tags?.find((d) => d.id === tag.parentTagId);
              // return `${resource.name} ${(this.value ?? 0) === 0 ? 'does not have' : 'has'} tag '${parentTag?.label} > ${tag.label}' in common`;
            }
            return '';
          },
        },
        data: metrics.map((metric) => {
          const metricScore = data.scores.find(score => score.metricName === metric && score.stakeholderId === stakeholder.surveyStakeholderId);
          if (metricScore?.countResponses) {
            return metricScore.sumValue / metricScore.countResponses;
          }
          return undefined;
        }),
      }
    }),
    // series: [
    //   {
    //     type: 'column',
    //     name: 'Stakeholders',
    //     colorByPoint: true,
    //     data: rootStakeholders.map(s => {
    //       const anySubstakeholders = data.stakeholders
    //         .find(sub => sub.parentSurveyStakeholderId === s.surveyStakeholderId);
    //       // console.log('stakeholder vs. anysubs?', s, anySubstakeholders);
    //       const metricScores = data.scores.filter(score => score.stakeholderId === s.surveyStakeholderId);
    //       const sumScores = metricScores.map(metricScore => metricScore.sumValue).reduce((a, b) => a + b);
    //       const countResponses = metricScores.map(metricScore => metricScore.countResponses).reduce((a, b) => a + b)
    //       return {
    //         name: s.name,
    //         y: countResponses > 0 ? sumScores / countResponses : undefined,
    //         drilldown: anySubstakeholders ? s.name : undefined,
    //       };
    //     }),
    //   },
    // ],
    // drilldown: {
    //   breadcrumbs: {
    //     position: {
    //       align: 'right'
    //     }
    //   },
    //   series: parentStakeholders.map((parent) => {
    //     const subStakeholders = data.stakeholders
    //       .filter(sub => sub.parentSurveyStakeholderId === parent.surveyStakeholderId);
    //     return {
    //       name: parent.name,
    //       id: parent.name,
    //       type: 'column',
    //       colorByPoint: true,
    //       data: subStakeholders.map((sub) => {
    //         const metricScores = data.scores.filter(score => score.stakeholderId === sub.surveyStakeholderId);
    //         const sumScores = metricScores.map(metricScore => metricScore.sumValue).reduce((a, b) => a + b);
    //         const countResponses = metricScores.map(metricScore => metricScore.countResponses).reduce((a, b) => a + b)

    //         return {
    //           name: sub.name,
    //           //type: 'xrange',
    //           y: countResponses > 0 ? sumScores / countResponses : undefined,
    //         };
    //       }),
    //     };
    //   }
    //   ),
    // },
  };

  return <div className="my-3">
    <div className="card">
      <div className="card-body">
        {chartOptions && <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartComponentRef} />}
      </div>
    </div>
  </div>;
}

function MetricSubScale(props: OverallSummaryProps & { report: SurveyReport; reportSchool: SurveyReportSchool }) {
  const { report, reportSchool, domain, selectedMetric, onMetricSelected } = props;
  const [detailData, setDetailData] = useState<SurveyReportDataMetricDetailResponse>();
  const [isLoading, setIsLoading] = useState(false);
  const [pivoted, setPivoted] = useState(false);
  const [expandedStakeholderIds, setExpandedStakeholderIds] = useState<Set<number>>(new Set());

  const toggleStakeholderExpansion = (stakeholderId: number | undefined) => {
    if (!stakeholderId) {
      return;
    }

    setExpandedStakeholderIds((previous) => {
      const next = new Set(previous);
      if (next.has(stakeholderId)) {
        next.delete(stakeholderId);
      } else {
        next.add(stakeholderId);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!selectedMetric) {
      setDetailData(undefined);
      setExpandedStakeholderIds(new Set());
      return;
    }
    setIsLoading(true);
    setDetailData(undefined);
    setExpandedStakeholderIds(new Set());
    api.survey(report.surveyId).report(report.id).school(reportSchool.id!)
      .metricDetail(selectedMetric.id, {
        domain: domain,
        school: reportSchool.id!,
      })
      .then((data) => {
        setDetailData(data);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [selectedMetric, domain, report.id, report.surveyId, reportSchool.id]);

  return <div className="my-3">
    <div className="card">
      <div className="card-body">
        <div className="row">
          <div className="col-3">
            <label>Scale</label>
            <div><select className="form-control"><option>{props.domain}</option></select>
            </div>
          </div>
          <div className="col-3">
            <label>Sub-scale</label>
            <div><select className="form-control" value={selectedMetric?.name ?? ''} onChange={(e) => onMetricSelected && onMetricSelected(e.target.value)}>
              <option value=""> -- Select Sub-scale --</option>
              {[...new Set(props.data.scores.map(score => score.metricName))].map(subScale => <option key={subScale} value={subScale}>{subScale}</option>)}
            </select>
            </div>
          </div>
          <div className="col-6 text-right text-end">
            {selectedMetric &&
              <MetricSubScaleExportButton
                report={report}
                reportSchool={reportSchool}
                domain={domain}
                selectedMetric={selectedMetric}
              />}
          </div>
        </div>
      </div>
    </div>
    {selectedMetric && <div className="card mt-2">
      <div className="card-header">
        <div className="row mb-2">
          <div className="col-12 d-flex justify-content-between align-items-center">
            <h4 className="mb-0">{selectedMetric.name}</h4>
            <div className="btn-group" role="group">
              <button type="button" className={`btn btn-sm ${!pivoted ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setPivoted(false)}>By Choice</button>
              <button type="button" className={`btn btn-sm ${pivoted ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setPivoted(true)}>By Stakeholder</button>
            </div>
          </div>
        </div>
      </div>
      <div className="card-body">
        {isLoading && <div className="alert alert-warning"><i className="fa-solid fa-spin fa-spinner"></i> <strong>Loading...</strong></div>}
        {detailData && detailData.questions.map((question) => {
          const choiceIds = question.choices.map(choice => choice.id);
          const stakeholdersById = new Map(
            detailData.stakeholders.map(s => [s.surveyStakeholderId ?? 0, s])
          );

          const stakeholderChartName = (stakeholder: SurveyReportStakeholder): string => {
            const stakeholderName = stakeholder.name ?? '';
            const parentId = stakeholder.parentSurveyStakeholderId ?? 0;
            if (parentId === 0) {
              return stakeholderName;
            }

            const parentName = stakeholdersById.get(parentId)?.name ?? '';
            return parentName ? `${parentName}: ${stakeholderName}` : stakeholderName;
          };

          const subStakeholdersByParentId = detailData.stakeholders.reduce<Map<number, SurveyReportStakeholder[]>>((map, stakeholder) => {
            const parentId = stakeholder.parentSurveyStakeholderId ?? 0;
            if (parentId === 0) {
              return map;
            }

            const existing = map.get(parentId) ?? [];
            existing.push(stakeholder);
            map.set(parentId, existing);
            return map;
          }, new Map<number, SurveyReportStakeholder[]>());

          const topLevelStakeholders = detailData.stakeholders.filter(s => (s.parentSurveyStakeholderId ?? 0) === 0);
          const visibleStakeholders = topLevelStakeholders.flatMap((stakeholder) => {
            const stakeholderId = stakeholder.surveyStakeholderId ?? 0;
            const subStakeholders = subStakeholdersByParentId.get(stakeholderId) ?? [];
            const isExpanded = expandedStakeholderIds.has(stakeholderId);

            return isExpanded ? [stakeholder, ...subStakeholders] : [stakeholder];
          });

          const stakeholderTotals = new Map(
            detailData.stakeholders.map(s => [
              s.surveyStakeholderId ?? 0,
              question.responses
                .filter(r => r.stakeholderId === s.surveyStakeholderId && choiceIds.includes(r.choiceId))
                .reduce((sum, r) => sum + (r.count ?? 0), 0),
            ])
          );

          const pct = (stakeholderId: number | undefined, choiceId: number): number => {
            const id = stakeholderId ?? 0;
            const total = stakeholderTotals.get(id) ?? 0;
            if (total === 0) return 0;
            const response = question.responses.find(r => r.stakeholderId === stakeholderId && r.choiceId === choiceId);
            return Math.round(((response?.count ?? 0) / total) * 1000) / 10;
          };

          const chartOptions: Highcharts.Options = {
            chart: { type: 'column' },
            title: { text: undefined },
            xAxis: {
              categories: pivoted
                ? visibleStakeholders.map(s => stakeholderChartName(s))
                : question.choices.map(c => c.choiceText ?? ''),
              title: { text: pivoted ? 'Stakeholder' : 'Choice' },
            },
            yAxis: {
              min: 0,
              max: 100,
              allowDecimals: true,
              title: { text: '% of Responses' },
              labels: { format: '{value}%' },
            },
            tooltip: {
              shared: true,
              valueSuffix: '%',
              valueDecimals: 1,
            },
            plotOptions: {
              column: {
                dataLabels: {
                  enabled: true,
                  format: '{y:.1f}%',
                },
              },
            },
            series: pivoted
              ? question.choices.map(choice => ({
                type: 'column' as const,
                name: choice.choiceText,
                data: visibleStakeholders.map(s => pct(s.surveyStakeholderId, choice.id)),
              }))
              : visibleStakeholders.map(s => ({
                type: 'column' as const,
                name: stakeholderChartName(s),
                data: question.choices.map(choice => pct(s.surveyStakeholderId, choice.id)),
              })),
          };
          return <div key={question.questionId} className="mt-3 border-bottom">
            <h4 className="text-center">{selectedMetric.name}: {question.questionText}</h4>
            <table className="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Choice</th>
                  {visibleStakeholders.map((s) => {
                    const stakeholderId = s.surveyStakeholderId ?? 0;
                    const subStakeholders = subStakeholdersByParentId.get(stakeholderId) ?? [];
                    const hasSubStakeholders = subStakeholders.length > 0;
                    const isExpanded = expandedStakeholderIds.has(stakeholderId);
                    const isSubStakeholder = (s.parentSurveyStakeholderId ?? 0) !== 0;
                    const parentStakeholder = detailData.stakeholders.find(st => st.surveyStakeholderId === (s.parentSurveyStakeholderId ?? 0));

                    return <th className="text-end" key={`${stakeholderId}-${s.name}`}>
                      {isSubStakeholder ? `- ${s.name}` : s.name}
                      {isSubStakeholder && parentStakeholder && <span className="text-muted"> {parentStakeholder.name}</span>}
                      {hasSubStakeholders &&
                        <button
                          type="button"
                          className="btn btn-sm p-0 ms-1 align-baseline"
                          onClick={() => toggleStakeholderExpansion(s.surveyStakeholderId)}
                          aria-label={isExpanded ? `Collapse ${s.name}` : `Expand ${s.name}`}>
                          {isExpanded ? '[-]' : '[+]'}
                        </button>}
                    </th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {question.choices.map(choice => (
                  <tr key={choice.id}>
                    <td>{choice.choiceText}</td>
                    {visibleStakeholders.map(s => {
                      const response = question.responses.find(r => r.stakeholderId === s.surveyStakeholderId && r.choiceId === choice.id);
                      return <td className="text-end" key={`${s.surveyStakeholderId}-${s.name}`}>{response?.count ?? 0}</td>;
                    })}
                  </tr>
                ))}
                {/* <tr>
                                    <td><i>Unanswered</i></td>
                                    {detailData.stakeholders.map(s => {
                                        const response = question.responses.find(r => r.stakeholderId === s.surveyStakeholderId && !choiceIds.includes(r.choiceId));
                                        return <td className="text-end" key={s.surveyStakeholderId}>{response?.count ?? 0}</td>;
                                    })}
                                </tr> */}
              </tbody>
              <tfoot>
                <tr>
                  <th>Total</th>
                  {visibleStakeholders.map(s => {
                    const total = question.responses
                      .filter(r => r.stakeholderId === s.surveyStakeholderId && choiceIds.includes(r.choiceId))
                      .reduce((sum, r) => sum + (r.count ?? 0), 0);
                    return <th className="text-end" key={`${s.surveyStakeholderId}-${s.name}`}>{total}</th>;
                  })}
                </tr>
              </tfoot>
            </table>
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          </div>
        })}
      </div>
    </div>}
  </div>;
}

type MetricSubScaleExportButtonProps = {
  report: SurveyReport;
  reportSchool: SurveyReportSchool;
  domain: string;
  selectedMetric: SurveyReportMetric;
};

function MetricSubScaleExportButton(props: MetricSubScaleExportButtonProps) {
  const { report, reportSchool, domain, selectedMetric } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [downloadFile, setDownloadFile] = useState<FileBlob>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    setIsLoading(false);
    setDownloadFile(undefined);
    setError(undefined);
  }, [selectedMetric.id, report.id, reportSchool.id, domain]);

  const onExport = () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    setError(undefined);
    setDownloadFile(undefined);

    api.survey(report.surveyId).report(report.id).school(reportSchool.id!)
      .metricExport(selectedMetric.id, {
        domain,
        school: reportSchool.id!,
      })
      .then((file) => {
        setDownloadFile(file);
        if (file.url) {
          window.open(file.url, '_blank', 'noopener,noreferrer');
        }
      })
      .catch(() => {
        setError('Unable to export sub-scale results. Please try again.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return <div className="d-inline-flex align-items-center gap-2">
    {downloadFile?.url === undefined && <button
      type="button"
      className="btn btn-sm btn-outline-success"
      onClick={onExport}
      disabled={isLoading}>
      {isLoading
        ? <><i className="fa-solid fa-spin fa-spinner me-1"></i>Exporting...</>
        : <><i className="fas fa-file-excel me-1"></i>Export Sub-scale Results</>}
    </button>}
    {!isLoading && downloadFile?.url &&
      <a
        href={downloadFile.url}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-sm btn-success">
        <i className="fa-solid fa-download me-1"></i>Download Ready
      </a>}
    {!isLoading && error && <small className="text-danger">{error}</small>}
  </div>;
}